import { useState } from 'react'
import { Button, Input, buttonActionIcon, Header, SubHeader, logo, cn } from '@valiant/ui-shared'
import { toast } from 'sonner'

interface SuperAdminImpersonateStepProps {
  onImpersonate: (email: string) => Promise<void>
  onCancel: () => void
}

export function SuperAdminImpersonateStep({
  onImpersonate,
  onCancel,
}: SuperAdminImpersonateStepProps) {
  const [email, setEmail] = useState('')
  const [callState, setCallState] = useState<'disabled' | 'action' | 'loading' | 'error'>(
    'disabled',
  )

  const LogoComponent = logo({})

  const handleEmailChange = (value: string) => {
    setEmail(value)
    if (callState !== 'loading') {
      setCallState(value.length > 0 ? 'action' : 'disabled')
    }
  }

  const handleSubmit = async () => {
    if (!email || callState === 'loading') return
    try {
      setCallState('loading')
      await onImpersonate(email)
    } catch (e) {
      setCallState('error')
      toast.error((e as Error).message || 'User not found')
    }
  }

  const buttonVariant =
    callState === 'action'
      ? 'square_active'
      : callState === 'error'
        ? 'square_error'
        : 'square_disabled'

  return (
    <div className="w-full h-full">
      <div className="flex items-start md:items-center justify-center py-16 md:py-12 min-h-screen">
        <div className="md:bg-white grid w-full gap-6 md:w-[425px] md:rounded-3xl p-6 md:mx-auto md:p-[64px] md:shadow-[0px_17px_56.8px_color-mix(in_srgb,var(--foreground)_11%,transparent),43px_62px_138.3px_color-mix(in_srgb,var(--main)_24%,transparent)]">
          <div className="grid gap-2 justify-center items-center">
            <div className="rounded-[15px] md:shadow-[4px_6px_10.2px_color-mix(in_srgb,var(--main)_65%,transparent),0px_13px_18.9px_color-mix(in_srgb,var(--main)_23%,transparent)]">
              <LogoComponent />
            </div>
          </div>
          <div className="grid gap-6">
            <Header title="Login as user" />
            <SubHeader title="Enter the email address of the user you want to login as." />
            <div className="grid gap-2">
              <div className="relative">
                <Input
                  id="impersonate-email"
                  type="email"
                  placeholder="User email"
                  className="pr-16"
                  value={email}
                  onChange={(e) => handleEmailChange(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSubmit()
                  }}
                  autoFocus
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <Button type="submit" variant={buttonVariant} size="icon" onClick={handleSubmit}>
                    <div className={cn(callState === 'loading' && 'animate-spin')}>
                      {buttonActionIcon(callState)}
                    </div>
                  </Button>
                </div>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onCancel} className="text-muted-foreground">
              Back to login
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
